import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import {
  emptyState, emptyGuestProfile, applyGuestVote, applyGuestBlockSense, applyGuestDishSense, toAccountPayload, memberProfilesFromNames
} from '../lib/onboardingState.js';

const KEY = 'clocook_onboarding_v1';

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...emptyState(), ...JSON.parse(raw) };
  } catch (e) { /* ignore */ }
  return emptyState();
}

export const useOnboardingStore = defineStore('onboarding', () => {
  const init = load();
  const introSeen = ref(init.introSeen);
  const guestProfiles = ref(init.guestProfiles);
  const orderDraft = ref(init.orderDraft);
  // Pose a la sortie du flow (commande atteinte via done ou Passer).
  // Tant que null : le hook cinematique se rejoue a chaque lancement.
  const flowCompletedAt = ref(init.flowCompletedAt || null);

  function persist() {
    try {
      localStorage.setItem(KEY, JSON.stringify({
        introSeen: introSeen.value,
        guestProfiles: guestProfiles.value,
        orderDraft: orderDraft.value,
        flowCompletedAt: flowCompletedAt.value
      }));
    } catch (e) { /* quota/prive : on ignore */ }
  }
  watch([introSeen, guestProfiles, orderDraft, flowCompletedAt], persist, { deep: true });

  function setIntroSeen() { introSeen.value = true; }

  function markFlowCompleted() { flowCompletedAt.value = Date.now(); }

  function ensureDefaultGuestProfile({ name = 'Moi', regime = 'Omnivore' } = {}) {
    if (!guestProfiles.value.length) {
      guestProfiles.value = [emptyGuestProfile({ key: 'principal', name, regime, isDefault: true })];
    }
    return guestProfiles.value[0];
  }

  function upsertGuestProfile(profile) {
    const i = guestProfiles.value.findIndex((p) => p.key === profile.key);
    if (i === -1) guestProfiles.value = [...guestProfiles.value, profile];
    else { const copy = [...guestProfiles.value]; copy[i] = { ...copy[i], ...profile }; guestProfiles.value = copy; }
  }

  // Renomme le profil principal (ecran prenom de l'onboarding).
  function renamePrincipal(name) {
    const p = ensureDefaultGuestProfile();
    upsertGuestProfile({ ...p, key: 'principal', name: name || p.name, isDefault: true });
  }

  // Remplace les membres du foyer par la liste de prenoms (ecran foyer).
  function setMembersByNames(names) {
    ensureDefaultGuestProfile();
    guestProfiles.value = memberProfilesFromNames(names, guestProfiles.value);
  }

  // Mappe le payload du wizard (useProfileWizard.buildPayload) vers guestProfiles.
  function setGuestProfilesFromWizard(payload) {
    if (!payload) return;
    const principal = ensureDefaultGuestProfile({ name: payload.firstname || 'Moi', regime: payload.regime });
    upsertGuestProfile({
      ...principal, key: 'principal', name: payload.firstname || principal.name,
      regime: payload.regime || 'Omnivore', allergies: payload.allergies || {},
      aversions: payload.aversions || '', tastePicks: payload.tastePicks || {},
      avatarUrl: payload.avatarUrl || principal.avatarUrl || '',
      avatarIcon: payload.avatarIcon || principal.avatarIcon || '',
      avatarColor: payload.avatarColor || principal.avatarColor || '',
      isDefault: true
    });
    const members = Array.isArray(payload.householdProfiles) ? payload.householdProfiles : [];
    members.forEach((m, idx) => {
      const key = `member-${idx}`;
      const existing = guestProfiles.value.find((p) => p.key === key);
      upsertGuestProfile({
        ...(existing || emptyGuestProfile({ key, name: m.name, regime: m.regime })),
        key, name: m.name || `Membre ${idx + 1}`, ageGroup: m.ageGroup || 'adult',
        regime: m.regime || 'Omnivore', allergies: m.allergies || {}, aversions: m.aversions || '',
        tastePicks: m.tastePicks || {}, isDefault: false
      });
    });
    // Purge des membres orphelins (le wizard a soumis moins de membres qu'avant).
    // Sans ça, des "member-N" périmés resteraient et seraient migrés en profils fantômes.
    // On garde toujours 'principal'. Miroir du nettoyage des compteurs repas.
    guestProfiles.value = guestProfiles.value.filter((p) => {
      if (p.key === 'principal') return true;
      if (typeof p.key === 'string' && p.key.startsWith('member-')) {
        const idx = parseInt(p.key.split('-')[1], 10);
        return !(Number.isInteger(idx) && idx >= members.length);
      }
      return true;
    });
  }

  function setMeals(key, n) {
    orderDraft.value = { ...orderDraft.value, mealsByProfileKey: { ...orderDraft.value.mealsByProfileKey, [key]: Math.max(0, n) } };
  }
  function setExtras(id, qty) {
    const extras = (orderDraft.value.extras || []).filter((e) => e.id !== id);
    if (qty > 0) extras.push({ id, qty });
    orderDraft.value = { ...orderDraft.value, extras };
  }

  function recordGuestVote({ kind, itemId, vote, dishBlockIds = [], profileKey = 'principal' }) {
    ensureDefaultGuestProfile();
    const i = guestProfiles.value.findIndex((p) => p.key === profileKey);
    if (i === -1) return;
    const updated = applyGuestVote(guestProfiles.value[i], { kind, itemId, vote, dishBlockIds });
    const copy = [...guestProfiles.value]; copy[i] = updated; guestProfiles.value = copy;
  }

  // Correction manuelle depuis l'editeur de gouts (pas un vote de quiz).
  function setGuestBlockSense({ profileKey = 'principal', blockId, sense }) {
    const i = guestProfiles.value.findIndex((p) => p.key === profileKey);
    if (i === -1) return;
    const updated = applyGuestBlockSense(guestProfiles.value[i], { blockId, sense });
    const copy = [...guestProfiles.value]; copy[i] = updated; guestProfiles.value = copy;
  }

  // Correction d'un vote plat invité (retrait depuis « On ne te servira jamais »).
  function setGuestDishSense({ profileKey = 'principal', dishId, sense }) {
    const i = guestProfiles.value.findIndex((p) => p.key === profileKey);
    if (i === -1) return;
    const updated = applyGuestDishSense(guestProfiles.value[i], { dishId, sense });
    const copy = [...guestProfiles.value]; copy[i] = updated; guestProfiles.value = copy;
  }

  function toPayload() {
    return toAccountPayload({ introSeen: introSeen.value, guestProfiles: guestProfiles.value, orderDraft: orderDraft.value });
  }

  // Purge des donnees du parcours (profils invites, brouillon) apres leur
  // migration vers le compte. Les marqueurs introSeen / flowCompletedAt sont
  // CONSERVES : les effacer rejouait le film et tout l'onboarding a la
  // prochaine deconnexion. Le watcher persiste l'etat restant.
  function clear() {
    guestProfiles.value = [];
    orderDraft.value = { mealsByProfileKey: {}, extras: [] };
  }

  // Remise a zero complete, marqueurs compris (rejoue le parcours au prochain
  // lancement). A reserver a une demande explicite ; aucun appelant pour l'instant.
  function reset() {
    clear();
    introSeen.value = false;
    flowCompletedAt.value = null;
    try { localStorage.removeItem(KEY); } catch (e) { /* ignore */ }
  }

  return {
    introSeen, guestProfiles, orderDraft, flowCompletedAt,
    setIntroSeen, markFlowCompleted, ensureDefaultGuestProfile, upsertGuestProfile, setGuestProfilesFromWizard,
    renamePrincipal, setMembersByNames,
    setMeals, setExtras, recordGuestVote, setGuestBlockSense, setGuestDishSense, toPayload, clear, reset
  };
});
